import { useEffect } from 'react';
import { BlogPost } from '../../utils/blogUtils';

interface BlogPostMetaProps {
  post: BlogPost;
}

const BlogPostMeta = ({ post }: BlogPostMetaProps) => {
  useEffect(() => {
    const baseUrl = window.location.origin;
    const postUrl = `${baseUrl}/resources/${post.slug}`;
    const imageUrl = post.image
      ? `${baseUrl}${post.image.startsWith('/') ? '' : '/'}${post.image}`
      : `${baseUrl}/favicon.ico`;

    // Update or create meta tags
    const updateMetaTag = (property: string, content: string) => {
      let element = document.querySelector(
        `meta[property="${property}"]`,
      ) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateNameMetaTag = (name: string, content: string) => {
      let element = document.querySelector(
        `meta[name="${name}"]`,
      ) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Open Graph tags for social sharing
    updateMetaTag('og:title', post.title);
    updateMetaTag('og:description', post.excerpt);
    updateMetaTag('og:image', imageUrl);
    updateMetaTag('og:url', postUrl);
    updateMetaTag('og:type', 'article');
    updateMetaTag('og:site_name', 'Practist');

    // Twitter Card tags
    updateNameMetaTag('twitter:card', 'summary_large_image');
    updateNameMetaTag('twitter:title', post.title);
    updateNameMetaTag('twitter:description', post.excerpt);
    updateNameMetaTag('twitter:image', imageUrl);

    // Standard meta tags
    updateNameMetaTag('description', post.excerpt);

    // Update page title
    document.title = `${post.title} | Practist Resources`;

    // Add structured data for SEO (JSON-LD)
    let structuredDataScript = document.getElementById('blog-structured-data');
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.id = 'blog-structured-data';
      structuredDataScript.type = 'application/ld+json';
      document.head.appendChild(structuredDataScript);
    }

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      image: imageUrl,
      datePublished: post.date,
      author: {
        '@type': 'Person',
        name: post.author,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Practist',
        logo: {
          '@type': 'ImageObject',
          url: `${baseUrl}/favicon.ico`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': postUrl,
      },
    };

    // Add podcast-specific structured data if applicable
    if (post.podcastEpisode) {
      structuredData['@type'] = 'PodcastEpisode';
      structuredData.episodeNumber = post.podcastEpisode.episodeNumber;
      if (post.podcastEpisode.guestName) {
        structuredData.guest = {
          '@type': 'Person',
          name: post.podcastEpisode.guestName,
        };
      }
    }

    structuredDataScript.textContent = JSON.stringify(structuredData);

    // Cleanup function
    return () => {
      document.title = 'Practist - Legal Practice Management Software';
    };
  }, [post]);

  return null;
};

export default BlogPostMeta;
